import { ReactNode } from "react";

import {
    Column,
    Icon,
    IconButton,
    Row,
    TagWrapper,
    Text,
    TextExternalLink,
    TrackingInfo,
    useAppPalette
} from "@bridgesplit/ui";
import { useLocalStorage } from "@bridgesplit/react";

import { DESKTOP_PADDING, MOBILE_PADDING, NAV_BREAKPOINT, useNavbarHeight } from "../navbar";
import { MERGER_ANNOUNCEMENT_URL, MERGER_FAQ_URL } from "../../constants";
import { useTrack } from "../../hooks";
import { AppTracking, TrackEvent } from "../../types";

const MERGER_BANNER_DISMISSED_KEY = "merger-banner-dismissed";
const DISMISSED_COOKIE_DAYS = 365;

export default function MergerBanner() {
    const { height } = useNavbarHeight();
    const { background } = useAppPalette();
    const { track } = useTrack();
    const [dismissed, setDismissed] = useLocalStorage(MERGER_BANNER_DISMISSED_KEY, false, {
        expires: DISMISSED_COOKIE_DAYS
    });

    if (dismissed) return null;

    return (
        <Column sx={{ mt: height + "px", mb: -height + "px", background }}>
            <TagWrapper
                sx={{ px: DESKTOP_PADDING, borderRadius: 0, [NAV_BREAKPOINT.below]: { px: MOBILE_PADDING } }}
                color="info"
            >
                <Row spacing={1} spaceBetween>
                    <Row spacing={1}>
                        <Text variant="body2" svgColor="info">
                            <Icon type="sparkles" />
                        </Text>
                        <Text variant="body2" sx={{ display: "inline" }}>
                            Orca and Loopscale have merged! Read more in the{" "}
                            <MergerLink
                                href={MERGER_ANNOUNCEMENT_URL}
                                tracking={AppTracking.from(TrackEvent.ClickMergerAnnouncement)}
                            >
                                announcement
                            </MergerLink>{" "}
                            and{" "}
                            <MergerLink href={MERGER_FAQ_URL} tracking={AppTracking.from(TrackEvent.ClickMergerFaq)}>
                                FAQ
                            </MergerLink>
                            .
                        </Text>
                    </Row>
                    <IconButton
                        border={false}
                        icon="reject"
                        textVariant="body2"
                        onClick={() => {
                            track(TrackEvent.DismissMergerBanner);
                            setDismissed(true);
                        }}
                    />
                </Row>
            </TagWrapper>
        </Column>
    );
}

function MergerLink({ href, tracking, children }: { href: string; tracking: TrackingInfo; children: ReactNode }) {
    return (
        <TextExternalLink href={href} tracking={tracking} sx={{ display: "inline" }} isPrimary underlineLink>
            {children}
        </TextExternalLink>
    );
}
